import type { StripeOnboardingFormStep } from "./steps";

/**
 * Where a field the backend rejected lives: the step that owns it and the
 * form field name inside that step (null on steps with no typed field).
 * Paths are `MultipleInputFieldsProblem.problems[].field` values.
 */
export interface ServerFieldTarget {
  step: StripeOnboardingFormStep;
  fieldName: string | null;
}

const company = (fieldName: string | null): ServerFieldTarget => ({
  step: "company-details",
  fieldName,
});
const category = (fieldName: string | null): ServerFieldTarget => ({
  step: "category-and-mcc",
  fieldName,
});
const personal = (fieldName: string | null): ServerFieldTarget => ({
  step: "personal-details",
  fieldName,
});
const payout = (fieldName: string | null): ServerFieldTarget => ({
  step: "payout-and-terms",
  fieldName,
});

const EXACT: Readonly<Record<string, ServerFieldTarget>> = {
  "input.account.country": { step: "country", fieldName: null },
  "input.account.businessType": { step: "work-type", fieldName: null },
  "input.account.company.structure": { step: "structure", fieldName: null },

  "input.account.company.name": company("companyName"),
  "input.account.company.taxId": company("companyTaxIdKvk"),
  "input.account.company.phone": company("companyPhone"),
  "input.account.company.address.line1": company("companyAddressLine1"),
  "input.account.company.address.city": company("companyAddressCity"),
  "input.account.company.address.postalCode": company(
    "companyAddressPostalCode",
  ),

  "input.account.businessProfile.mcc": category("businessMcc"),
  "input.account.businessProfile.url": category("businessUrl"),
  "input.account.businessProfile.productDescription": category(
    "businessDescription",
  ),

  "input.representative.firstName": personal("representativeFirstName"),
  "input.representative.lastName": personal("representativeLastName"),
  "input.representative.email": personal("representativeEmail"),
  "input.representative.phone": personal("representativePhone"),
  "input.representative.dob.day": personal("representativeDobDay"),
  "input.representative.dob.month": personal("representativeDobMonth"),
  "input.representative.dob.year": personal("representativeDobYear"),
  "input.representative.address.line1": personal("representativeAddressLine1"),
  "input.representative.address.city": personal("representativeAddressCity"),
  "input.representative.address.postalCode": personal(
    "representativeAddressPostalCode",
  ),
  "input.representative.nationality": personal("representativeNationality"),
  "input.representative.title": personal("representativeTitle"),
  "input.representative.idNumber": personal("idNumber"),

  "input.externalAccount.accountNumber": payout("payoutIban"),
};

const PREFIX_ENTRIES: readonly (readonly [string, ServerFieldTarget])[] = [
  ["input.account.company", company(null)],
  ["input.account.businessProfile", category(null)],
  ["input.representative", personal(null)],
  ["input.persons", { step: "beneficial-owners", fieldName: null }],
  ["input.externalAccount", payout(null)],
  ["input.account.tosAcceptance", payout(null)],
];

/**
 * Longest prefix first, sorted here rather than left to whoever edits the
 * list above. The matcher returns on the first hit, so a nested prefix added
 * later (`input.account.company.address`, say) beats its parent wherever it
 * is written. The six above are disjoint, so nothing depends on the order
 * today — which is why it takes a sort and not a note to stay that way.
 */
const PREFIXES: readonly (readonly [string, ServerFieldTarget])[] = [
  ...PREFIX_ENTRIES,
].sort(([a], [b]) => b.length - a.length);

/** Null when the path is unknown: the caller stays on the review step and shows the message there. */
export function resolveServerFieldRejection(
  field: string,
): ServerFieldTarget | null {
  const exact = EXACT[field];
  if (exact) return exact;
  for (const [prefix, target] of PREFIXES) {
    if (field === prefix || field.startsWith(`${prefix}.`)) return target;
  }
  return null;
}

const hasFieldName = (
  target: ServerFieldTarget,
): target is ServerFieldTarget & { fieldName: string } =>
  target.fieldName !== null;

const STEP_BY_DRAFT_FIELD: Readonly<Record<string, StripeOnboardingFormStep>> =
  Object.fromEntries(
    Object.values(EXACT)
      .filter(hasFieldName)
      .map((target) => [target.fieldName, target.step]),
  );

/**
 * The step that owns a draft field, for a failed whole-draft validation on
 * the review step. Owners and the terms box have no single field in the map.
 */
export function stepForDraftField(
  fieldName: string,
): StripeOnboardingFormStep | null {
  if (fieldName === "owners") return "beneficial-owners";
  if (fieldName === "tosAccepted") return "payout-and-terms";
  if (fieldName === "companyStructure") return "structure";
  return STEP_BY_DRAFT_FIELD[fieldName] ?? null;
}
